import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandItem
} from './ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';
import {
  getCallClassificationTypes,
  fetchEmployeeDailyWorkload,
  fetchEmployeeEfficiency,
  fetchEmployeeUpsellFunnel,
  fetchEmployeeKpi,
  fetchEmployeeCallsDynamics,
  fetchEmployeeQualityTrend,
  fetchEmployeeHourlyWorkload,
  fetchEmployeeHourlyWorkloadByDay,
  fetchEmployeeHourlyWaitWorkload
} from '../api';
import { getTexts } from '../utils/configLoader';
import useEmployees from '../hooks/useEmployees';
import useTimeZone from '../hooks/useTimeZone';
import { formatDayMonth } from '../utils/dateTime';
import { TrendingUp, Users, Clock, Activity, LineChart as LineChartIcon, BarChart3 } from 'lucide-react';
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList,
  BarChart as ReBarChart,
  Bar
} from 'recharts';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';

const chartPalette = ['#2563eb', '#db2777', '#16a34a', '#f97316', '#7c3aed', '#0ea5e9', '#facc15', '#14b8a6', '#ef4444', '#64748b'];

const CALL_CLASSIFICATION_LABELS = {
  order_new: 'Новый заказ',
  order_confirm_outbound: 'Подтверждение заказа',
  order_change: 'Изменение заказа',
  delivery_issue: 'Жалоба',
  consultation: 'Консультация',
  callback_missed: 'Пропущенный дозвон',
  wrong_number: 'Ошибочный номер'
};

const formatCallClassificationType = (value) => {
  if (!value) {
    return '—';
  }

  const key = String(value).trim().toLowerCase();
  if (!key) {
    return '—';
  }

  if (CALL_CLASSIFICATION_LABELS[key]) {
    return CALL_CLASSIFICATION_LABELS[key];
  }

  return key
    .split('_')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateLabel = (value, timeZone) => {
  if (!value) {
    return '—';
  }

  try {
    if (value instanceof Date || typeof value === 'string') {
      const formatted = formatDayMonth(value, timeZone);
      return formatted === '—' ? String(value) : formatted;
    }
    return String(value);
  } catch {
    return value;
  }
};

const prepareMultiSeriesData = (points, timeZone) => {
  if (!points?.length) {
    return { data: [], series: [] };
  }

  const seriesSet = new Set();
  points.forEach((point) => {
    Object.entries(point.values ?? {}).forEach(([name, value]) => {
      if (value !== null && value !== undefined) {
        seriesSet.add(name || '—');
      }
    });
  });

  const series = Array.from(seriesSet).sort((a, b) =>
    a.localeCompare(b, 'ru', { sensitivity: 'base' })
  );

  const data = points.map((point) => {
    const row = { label: formatDateLabel(point.day?.toString?.() ?? point.day, timeZone) };
    series.forEach((key) => {
      const value = point.values?.[key];
      row[key] = typeof value === 'number' ? Number(value) : null;
    });
    return row;
  });

  return { data, series };
};

const ChartEmptyState = ({ message = 'Нет данных для отображения' }) => (
  <div className="flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
    {message}
  </div>
);

const callsAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : value ?? '—';
const callsTooltipFormatter = (value) =>
  typeof value === 'number' ? `${value.toLocaleString('ru-RU')} звонков` : '—';
const qualityAxisFormatter = (value) =>
  typeof value === 'number' ? value.toFixed(1) : value ?? '—';
const qualityTooltipFormatter = (value) =>
  typeof value === 'number' ? `${value.toFixed(1)} балл` : '—';
const minutesAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : value ?? '—';
const minutesTooltipFormatter = (value) =>
  typeof value === 'number' ? `${value.toLocaleString('ru-RU')} мин` : '—';

const createLabelRenderer = (color, formatter) => (props) => {
  const { x, y, value, width } = props;
  if (value === null || value === undefined) {
    return null;
  }

  const baseX = typeof x === 'number' ? x : 0;
  const centerX = typeof width === 'number' ? baseX + width / 2 : baseX;

  return (
    <text
      x={centerX}
      y={(y ?? 0) - 8}
      textAnchor="middle"
      fill={color}
      fontSize="11"
      fontWeight="600"
    >
      {formatter ? formatter(value) : value}
    </text>
  );
};

const resolveConversionStyle = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { label: '—', className: 'bg-muted text-muted-foreground' };
  }

  const rate = Math.max(0, Math.min(100, value));

  if (rate < 20) return { label: `${rate.toFixed(1)}%`, className: 'bg-red-100 text-red-800' };
  if (rate < 40) return { label: `${rate.toFixed(1)}%`, className: 'bg-orange-100 text-orange-800' };
  if (rate < 60) return { label: `${rate.toFixed(1)}%`, className: 'bg-yellow-100 text-yellow-800' };
  if (rate < 80) return { label: `${rate.toFixed(1)}%`, className: 'bg-lime-100 text-lime-800' };
  return { label: `${rate.toFixed(1)}%`, className: 'bg-green-100 text-green-800' };
};

const tooltipWrapperStyle = { zIndex: 30 };

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
          <span>
            {item.name}: {formatter ? formatter(item.value, item.name) : item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const MultiLineChart = ({ data, series, axisFormatter, tooltipFormatter, labelFormatter }) => {
  if (!data?.length || !series?.length) {
    return <ChartEmptyState />;
  }

  return (
    <div className="h-64 w-full min-w-0">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
        <ReLineChart data={data} margin={{ top: 24, right: 24, left: 8, bottom: 12 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            width={60}
            tick={{ fontSize: 11 }}
            tickFormatter={axisFormatter}
            allowDecimals
          />
          <RechartsTooltip
            wrapperStyle={tooltipWrapperStyle}
            content={<ChartTooltip formatter={tooltipFormatter || axisFormatter} />}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          {series.map((key, index) => {
            const color = chartPalette[index % chartPalette.length];
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                connectNulls
              >
                <LabelList
                  dataKey={key}
                  content={createLabelRenderer(color, labelFormatter || axisFormatter)}
                />
              </Line>
            );
          })}
        </ReLineChart>
      </ResponsiveContainer>
    </div>
  );
};

const SingleBarChart = ({
  data,
  axisFormatter,
  tooltipFormatter,
  labelFormatter,
  seriesLabel = 'Значение'
}) => {
  if (!data?.length) {
    return <ChartEmptyState />;
  }

  const color = '#f59e0b';

  return (
    <div className="h-64 w-full min-w-0">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
        <ReBarChart data={data} margin={{ top: 24, right: 24, left: 8, bottom: 12 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis width={60} tick={{ fontSize: 11 }} tickFormatter={axisFormatter} />
          <RechartsTooltip
            wrapperStyle={tooltipWrapperStyle}
            content={<ChartTooltip formatter={tooltipFormatter || axisFormatter} />}
          />
          <Bar
            dataKey="value"
            name={seriesLabel}
            fill={color}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
          >
            <LabelList
              dataKey="value"
              content={createLabelRenderer(color, labelFormatter || axisFormatter)}
            />
          </Bar>
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
};

const EmployeeAnalytics = ({ dateRange }) => {
  const { timeZone } = useTimeZone();
  const texts = getTexts('employees');
  const { employees, error: employeesError } = useEmployees();

  const [selectedEmployees, setSelectedEmployees] = useState(['all']);
  const [selectedDepartments, setSelectedDepartments] = useState(['all']);
  const [selectedClassificationTypes, setSelectedClassificationTypes] = useState(['all']);
  const [classificationTypeOptions, setClassificationTypeOptions] = useState(['all']);
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isClassificationDropdownOpen, setIsClassificationDropdownOpen] = useState(false);

  const [kpi, setKpi] = useState(null);
  const [efficiency, setEfficiency] = useState([]);
  const [dailyWorkload, setDailyWorkload] = useState([]);
  const [hourlyWorkload, setHourlyWorkload] = useState([]);
  const [hourlyByDay, setHourlyByDay] = useState([]);
  const [hourlyWaitWorkload, setHourlyWaitWorkload] = useState([]);
  const [qualityTrend, setQualityTrend] = useState([]);
  const [callsDynamics, setCallsDynamics] = useState([]);
  const [employeeUpsellFunnel, setEmployeeUpsellFunnel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);
  const REFRESH_INTERVAL_MS = GRAPH_REFRESH_INTERVAL_MS;

  useEffect(() => {
    let isDisposed = false;

    const loadClassificationTypes = async () => {
      try {
        const response = await getCallClassificationTypes();
        if (isDisposed) {
          return;
        }

        const normalizedTypes = Array.from(
          new Set(
            (Array.isArray(response) ? response : [])
              .map((type) => (typeof type === 'string' ? type.trim().toLowerCase() : ''))
              .filter(Boolean)
          )
        ).sort((a, b) =>
          formatCallClassificationType(a).localeCompare(
            formatCallClassificationType(b),
            'ru',
            { sensitivity: 'base' }
          )
        );

        setClassificationTypeOptions(['all', ...normalizedTypes]);
        setSelectedClassificationTypes((prev) => {
          if (prev.includes('all')) {
            return ['all'];
          }

          const next = prev.filter((type) => normalizedTypes.includes(type));
          return next.length > 0 ? next : ['all'];
        });
      } catch (err) {
        console.error(err);
        if (!isDisposed) {
          setClassificationTypeOptions(['all']);
          setSelectedClassificationTypes(['all']);
        }
      }
    };

    loadClassificationTypes();

    return () => {
      isDisposed = true;
    };
  }, []);

  const loadData = useCallback(async (showSpinner = true) => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    if (isFetching.current) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    isFetching.current = true;
    setError(null);

    const baseParams = {
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd')
    };

    const actualClassificationTypes = selectedClassificationTypes.includes('all')
      ? []
      : selectedClassificationTypes.filter((type) => type !== 'all');
    const actualDepartments = selectedDepartments.includes('all')
      ? []
      : selectedDepartments.filter((department) => department !== 'all');
    const actualEmployees = selectedEmployees.includes('all')
      ? []
      : selectedEmployees.filter((id) => id !== 'all');

    const singleEmployee = actualEmployees.length === 1 ? actualEmployees[0] : undefined;
    const employeeFilter =
      actualEmployees.length > 1
        ? { employee_ids: actualEmployees }
        : singleEmployee
          ? { employee_id: singleEmployee }
          : {};
    const classificationTypeFilter =
      actualClassificationTypes.length > 0
        ? { call_classification_types: actualClassificationTypes }
        : {};
    const departmentFilter =
      actualDepartments.length > 0
        ? { departments: actualDepartments }
        : {};

    const commonParams = {
      ...baseParams,
      ...employeeFilter,
      ...departmentFilter,
      ...classificationTypeFilter
    };

    const upsellParams = {
      ...baseParams,
      ...(actualEmployees.length > 0 ? { employee_ids: actualEmployees } : {}),
      ...departmentFilter,
      ...classificationTypeFilter
    };

    try {
      const [
        kpiData,
        efficiencyData,
        workloadData,
        hourlyData,
        hoursByDayData,
        hourlyWaitData,
        callsData,
        qualityData,
        upsellData
      ] = await Promise.all([
        fetchEmployeeKpi(commonParams),
        fetchEmployeeEfficiency(commonParams),
        fetchEmployeeDailyWorkload(commonParams),
        fetchEmployeeHourlyWorkload(commonParams),
        fetchEmployeeHourlyWorkloadByDay(commonParams),
        fetchEmployeeHourlyWaitWorkload(commonParams),
        fetchEmployeeCallsDynamics(commonParams),
        fetchEmployeeQualityTrend(commonParams),
        fetchEmployeeUpsellFunnel(upsellParams)
      ]);

      setKpi(kpiData);
      setEfficiency(efficiencyData ?? []);
      setDailyWorkload(workloadData ?? []);
      setHourlyWorkload(hourlyData ?? []);
      setHourlyByDay(hoursByDayData ?? []);
      setHourlyWaitWorkload(hourlyWaitData ?? []);
      setCallsDynamics(callsData ?? []);
      setQualityTrend(qualityData ?? []);
      setEmployeeUpsellFunnel(upsellData?.items ?? []);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить аналитику по сотрудникам.');
    } finally {
      isFetching.current = false;
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  }, [dateRange?.from, dateRange?.to, selectedClassificationTypes, selectedDepartments, selectedEmployees]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadData(false);
    }, REFRESH_INTERVAL_MS);

    const handleClickOutside = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (
        target.closest('[data-employee-dropdown]')
        || target.closest('[data-classification-dropdown]')
        || target.closest('[data-department-dropdown]')
      ) {
        return;
      }

      setIsEmployeeDropdownOpen(false);
      setIsDepartmentDropdownOpen(false);
      setIsClassificationDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [loadData]);

  const employeeOptions = useMemo(() => {
    const options = employees.map((emp) => ({
      id: emp.id?.toString() ?? emp.name,
      name: emp.name ?? emp.fullName ?? 'Без имени',
      department: emp.department
    }));

    return options;
  }, [employees]);

  const departmentOptions = useMemo(
    () => [...new Set(employees.map((employee) => employee.department).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' })),
    [employees]
  );

  const filteredEmployeeOptions = useMemo(() => {
    if (selectedDepartments.includes('all')) {
      return employeeOptions;
    }

    return employeeOptions.filter(
      (employee) => employee.department && selectedDepartments.includes(employee.department)
    );
  }, [employeeOptions, selectedDepartments]);

  useEffect(() => {
    if (selectedEmployees.includes('all')) {
      return;
    }

    const allowedIds = new Set(filteredEmployeeOptions.map((employee) => employee.id));
    const nextSelected = selectedEmployees.filter((id) => allowedIds.has(id));

    if (nextSelected.length === selectedEmployees.length) {
      return;
    }

    setSelectedEmployees(nextSelected.length > 0 ? nextSelected : ['all']);
  }, [filteredEmployeeOptions, selectedEmployees]);

  const filteredUpsellItems = useMemo(() => {
    const items = Array.isArray(employeeUpsellFunnel) ? employeeUpsellFunnel : [];
    const selectedIds =
      selectedEmployees.includes('all') ? null : new Set(selectedEmployees.filter((id) => id !== 'all'));

    return items.filter((item) => {
      const id = item.employeeId ? item.employeeId.toString() : null;

      if (selectedIds && (!id || !selectedIds.has(id))) {
        return false;
      }

      return true;
    });
  }, [employeeUpsellFunnel, selectedEmployees]);

  const efficiencyRows = useMemo(() => {
    const upsellByEmployee = new Map(
      (Array.isArray(employeeUpsellFunnel) ? employeeUpsellFunnel : [])
        .filter((item) => item?.employeeId)
        .map((item) => [item.employeeId.toString(), item])
    );

    return (Array.isArray(efficiency) ? efficiency : []).map((row) => {
      const employeeKey = row?.employeeId ? row.employeeId.toString() : null;
      const upsell = employeeKey ? upsellByEmployee.get(employeeKey) : null;

      return {
        ...row,
        upsellAttempts: Number(upsell?.upsellAttempts) || 0,
        upsellSuccesses: Number(upsell?.upsellSuccesses) || 0,
        attemptRate: Number(upsell?.attemptRate) || 0,
        successRate: Number(upsell?.successRate) || 0
      };
    });
  }, [efficiency, employeeUpsellFunnel]);

  const teamUpsellMetrics = useMemo(() => {
    const totals = filteredUpsellItems.reduce(
      (acc, item) => {
        acc.orders += Number(item.totalOrders) || 0;
        acc.attempts += Number(item.upsellAttempts) || 0;
        acc.successes += Number(item.upsellSuccesses) || 0;
        return acc;
      },
      { orders: 0, attempts: 0, successes: 0 }
    );

    const attemptRate = totals.orders ? (totals.attempts * 100) / totals.orders : 0;
    const successRate = totals.attempts ? (totals.successes * 100) / totals.attempts : 0;

    return {
      ...totals,
      attemptRate: Math.round(attemptRate * 10) / 10,
      successRate: Math.round(successRate * 10) / 10
    };
  }, [filteredUpsellItems]);

  const qualityChartData = useMemo(
    () => prepareMultiSeriesData(qualityTrend, timeZone),
    [qualityTrend, timeZone]
  );

  const callsChartData = useMemo(
    () => prepareMultiSeriesData(callsDynamics, timeZone),
    [callsDynamics, timeZone]
  );

  const weekdayChartData = useMemo(
    () =>
      (dailyWorkload ?? []).map((point) => ({
        label: point.day,
        value: point.calls
      })),
    [dailyWorkload]
  );

  const hourlyChartData = useMemo(() => {
    const totals = Array(24).fill(0);

    (hourlyWorkload ?? []).forEach((point) => {
      const hour = Number(point.hour);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        return;
      }
      const calls = typeof point.calls === 'number' ? point.calls : Number(point.calls) || 0;
      totals[hour] += calls;
    });

    const activeHours = totals
      .map((value, hour) => ({ hour, value }))
      .filter((item) => item.value > 0);

    if (activeHours.length === 0) {
      return [];
    }

    return activeHours.map(({ hour, value }) => ({
      label: `${String(hour).padStart(2, '0')}:00`,
      value
    }));
  }, [hourlyWorkload]);

  const durationChartData = useMemo(() => {
    const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const totals = new Map();

    (hourlyByDay ?? []).forEach((point) => {
      const label = typeof point.day === 'string' ? point.day : '';
      const rawMinutes =
        typeof point.totalMinutes === 'number'
          ? point.totalMinutes
          : Number(point.totalMinutes) || 0;
      const valueMinutes = Math.round(rawMinutes * 10) / 10; // keep one decimal to avoid zeroing short calls

      if (!label) {
        return;
      }

      const existing = totals.get(label) ?? 0;
      totals.set(label, existing + valueMinutes);
    });

    return weekdays.map((label) => ({
      label,
      value: totals.get(label) ?? 0
    }));
  }, [hourlyByDay]);

  const waitByHourChartData = useMemo(() => {
    const totals = Array(24).fill(0);

    (hourlyWaitWorkload ?? []).forEach((point) => {
      const hour = Number(point.hour);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        return;
      }

      const minutes =
        typeof point.waitMinutes === 'number'
          ? point.waitMinutes
          : Number(point.waitMinutes) || 0;
      totals[hour] += Math.round(minutes * 10) / 10;
    });

    const activeHours = totals
      .map((value, hour) => ({ hour, value }))
      .filter((item) => item.value > 0);

    if (activeHours.length === 0) {
      return [];
    }

    return activeHours.map(({ hour, value }) => ({
      label: `${String(hour).padStart(2, '0')}:00`,
      value
    }));
  }, [hourlyWaitWorkload]);

  return (
    <div className="space-y-6">
      {employeesError && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          {employeesError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{texts.title}</CardTitle>
          <CardDescription>{texts.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="sm:flex-1">
            <p className="mb-1 text-sm text-muted-foreground">Типы звонков</p>
            <div className="relative" data-classification-dropdown>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between"
                onClick={() => setIsClassificationDropdownOpen((prev) => !prev)}
              >
                <span className="truncate">
                  {selectedClassificationTypes.includes('all')
                    ? 'Все типы звонков'
                    : selectedClassificationTypes.map((type) => formatCallClassificationType(type)).join(', ')}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {isClassificationDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full max-w-[260px] rounded-md border bg-white p-0 shadow-md">
                  <Command loop autoFocus={true}>
                    <CommandInput placeholder="Поиск типа звонка..." />
                    <CommandList>
                      <CommandEmpty>Не найдено</CommandEmpty>
                      <CommandGroup>
                        {classificationTypeOptions.map((type) => (
                          <CommandItem
                            key={type}
                            onSelect={() => {
                              setSelectedClassificationTypes((prev) => {
                                if (type === 'all') {
                                  return ['all'];
                                }
                                if (prev.includes('all')) {
                                  return [type];
                                }
                                if (prev.includes(type)) {
                                  const next = prev.filter((item) => item !== type);
                                  return next.length === 0 ? ['all'] : next;
                                }
                                return [...prev, type];
                              });
                            }}
                            className="flex items-center gap-2"
                          >
                            <Check
                              className={`h-4 w-4 ${selectedClassificationTypes.includes(type) ? 'opacity-100' : 'opacity-0'}`}
                            />
                            {type === 'all' ? 'Все типы звонков' : formatCallClassificationType(type)}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedClassificationTypes.includes('all') ? (
                <Badge variant="outline">Все типы звонков</Badge>
              ) : (
                selectedClassificationTypes.map((type) => (
                  <Badge key={type} variant="outline">
                    {formatCallClassificationType(type)}
                  </Badge>
                ))
              )}
            </div>
          </div>
          <div className="sm:flex-1" data-department-dropdown>
            <p className="mb-1 text-sm text-muted-foreground">Отдел</p>
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between"
                onClick={() => setIsDepartmentDropdownOpen((prev) => !prev)}
              >
                <span className="truncate">
                  {selectedDepartments.includes('all')
                    ? 'Все отделы'
                    : selectedDepartments.join(', ')}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {isDepartmentDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full max-w-[280px] rounded-md border bg-white p-0 shadow-md">
                  <Command loop autoFocus={true}>
                    <CommandInput placeholder="Поиск отдела..." />
                    <CommandList>
                      <CommandEmpty>Не найдено</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          key="all"
                          onSelect={() => {
                            setSelectedDepartments(['all']);
                            setSelectedEmployees(['all']);
                            setIsDepartmentDropdownOpen(false);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${selectedDepartments.includes('all') ? 'opacity-100' : 'opacity-0'}`}
                          />
                          Все отделы
                        </CommandItem>
                        {departmentOptions.map((department) => {
                          const isSelected = selectedDepartments.includes(department);
                          return (
                            <CommandItem
                              key={department}
                              onSelect={() => {
                                setSelectedDepartments((prev) => {
                                  if (prev.includes('all')) {
                                    return [department];
                                  }
                                  if (prev.includes(department)) {
                                    const next = prev.filter((item) => item !== department);
                                    return next.length === 0 ? ['all'] : next;
                                  }
                                  return [...prev, department];
                                });
                                setSelectedEmployees(['all']);
                              }}
                              className="flex items-center gap-2"
                            >
                              <Check className={`h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                              {department}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedDepartments.includes('all') ? (
                <Badge variant="outline">Все отделы</Badge>
              ) : (
                selectedDepartments.map((department) => (
                  <Badge key={department} variant="outline">
                    {department}
                  </Badge>
                ))
              )}
            </div>
          </div>
          <div className="sm:flex-1">
            <p className="mb-1 text-sm text-muted-foreground">Сотрудники</p>
            <div className="relative" data-employee-dropdown>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between"
                onClick={() => setIsEmployeeDropdownOpen((prev) => !prev)}
              >
                <span className="truncate">
                  {selectedEmployees.includes('all')
                    ? 'Все сотрудники'
                    : selectedEmployees
                        .map((id) => filteredEmployeeOptions.find((e) => e.id === id)?.name ?? employeeOptions.find((e) => e.id === id)?.name ?? id)
                        .join(', ')}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {isEmployeeDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full max-w-[360px] rounded-md border bg-white p-0 shadow-md">
                  <Command loop autoFocus={true}>
                    <CommandInput placeholder="Поиск сотрудника..." />
                    <CommandList>
                      <CommandEmpty>Не найдено</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          key="all"
                          onSelect={() => {
                            setSelectedEmployees(['all']);
                            setIsEmployeeDropdownOpen(false);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${selectedEmployees.includes('all') ? 'opacity-100' : 'opacity-0'}`}
                          />
                          Все сотрудники
                        </CommandItem>
                        {filteredEmployeeOptions.map((emp) => {
                          const isSelected = selectedEmployees.includes('all')
                            ? true
                            : selectedEmployees.includes(emp.id);
                          return (
                            <CommandItem
                              key={emp.id}
                              onSelect={() => {
                                setSelectedEmployees((prev) => {
                                  if (prev.includes('all')) {
                                    return [emp.id];
                                  }
                                  if (prev.includes(emp.id)) {
                                    const next = prev.filter((id) => id !== emp.id);
                                    return next.length === 0 ? ['all'] : next;
                                  }
                                  return [...prev, emp.id];
                                });
                              }}
                              className="flex items-center gap-2"
                            >
                              <Check className={`h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                              {emp.name}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedEmployees.includes('all') ? (
                <Badge variant="outline">Все сотрудники</Badge>
              ) : (
                selectedEmployees.map((id) => {
                  const emp = filteredEmployeeOptions.find((e) => e.id === id) ?? employeeOptions.find((e) => e.id === id);
                  return (
                    <Badge key={id} variant="outline">
                      {emp?.name ?? id}
                    </Badge>
                  );
                })
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <KpiCard
          title="В среднем звонков"
          description="В расчёте на сотрудника"
          value={formatMetric(kpi?.avgCallsPerEmployee)}
          icon={PhoneIcon}
        />
        <KpiCard
          title="Средняя длительность"
          description="Минуты/секунды"
          value={formatMetric(kpi?.avgCallDuration)}
          icon={Clock}
        />
        <KpiCard
          title="Среднее время разговора"
          description="Чистое общение"
          value={formatMetric(kpi?.avgTalkDuration)}
          icon={Clock}
        />
        <KpiCard
          title="Среднее ожидание"
          description="Дозвон и удержание"
          value={formatMetric(kpi?.avgWaitDuration)}
          icon={Clock}
        />
        <KpiCard
          title="Средний чек-лист"
          description="Балл по качеству"
          value={formatMetric(kpi?.avgQualityScore)}
          icon={TrendingUp}
        />
        <KpiCard
          title="Нагрузка"
          description="Количество сотрудников"
          value={employees.length}
          icon={Users}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Эффективность операторов</CardTitle>
          <CardDescription>Связка активности и качества по каждому сотруднику</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Сотрудник</TableHead>
                <TableHead>Звонки</TableHead>
                <TableHead>Качество</TableHead>
                <TableHead>Доля попыток допродаж</TableHead>
                <TableHead>Количество попыток допродаж</TableHead>
                <TableHead>Количество успешных допродаж</TableHead>
                <TableHead>Доля успешных допродаж</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {efficiencyRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
              {efficiencyRows.map((row) => (
                <TableRow key={row.employeeId ?? row.employeeName}>
                  <TableCell>{row.employeeName || '—'}</TableCell>
                  <TableCell>{row.calls ?? 0}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {row.quality != null && (
                        <Progress value={Math.min(100, row.quality * 10)} className="w-24" />
                      )}
                      <span>{row.quality?.toFixed(1) ?? '—'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const { label, className } = resolveConversionStyle(row.attemptRate);
                      return (
                        <Badge variant="outline" className={className}>
                          {label}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {Number(row.upsellAttempts || 0).toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell>
                    {Number(row.upsellSuccesses || 0).toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const { label, className } = resolveConversionStyle(row.successRate);
                      return (
                        <Badge variant="outline" className={className}>
                          {label}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <LineChartIcon className="h-4 w-4 text-muted-foreground" />
              Динамика качества работы
            </CardTitle>
            <CardDescription>Средний балл по каждому сотруднику в выбранном диапазоне</CardDescription>
          </CardHeader>
          <CardContent>
            <MultiLineChart
              data={qualityChartData.data}
              series={qualityChartData.series}
              axisFormatter={qualityAxisFormatter}
              labelFormatter={qualityAxisFormatter}
              tooltipFormatter={qualityTooltipFormatter}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <LineChartIcon className="h-4 w-4 text-muted-foreground" />
              Динамика звонков по сотрудникам
            </CardTitle>
            <CardDescription>Количество звонков по дням диапазона</CardDescription>
          </CardHeader>
          <CardContent>
            <MultiLineChart
              data={callsChartData.data}
              series={callsChartData.series}
              axisFormatter={callsAxisFormatter}
              labelFormatter={callsAxisFormatter}
              tooltipFormatter={callsTooltipFormatter}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2 2xl:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Нагрузка по дням недели
            </CardTitle>
            <CardDescription>Сумма звонков среди выбранных сотрудников</CardDescription>
          </CardHeader>
          <CardContent>
            <SingleBarChart
              data={weekdayChartData}
              axisFormatter={callsAxisFormatter}
              tooltipFormatter={callsTooltipFormatter}
              labelFormatter={callsAxisFormatter}
              seriesLabel="Звонки"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Нагрузка по часам
            </CardTitle>
            <CardDescription>Всего звонков по часам</CardDescription>
          </CardHeader>
          <CardContent>
            <SingleBarChart
              data={hourlyChartData}
              axisFormatter={callsAxisFormatter}
              tooltipFormatter={callsTooltipFormatter}
              labelFormatter={callsAxisFormatter}
              seriesLabel="Звонки"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Часовая нагрузка по дням
            </CardTitle>
            <CardDescription>Суммарная длительность звонков за день (минуты)</CardDescription>
          </CardHeader>
          <CardContent>
            <SingleBarChart
              data={durationChartData}
              axisFormatter={minutesAxisFormatter}
              tooltipFormatter={minutesTooltipFormatter}
              labelFormatter={minutesAxisFormatter}
              seriesLabel="Минуты"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Ожидание и удержание по часам
            </CardTitle>
            <CardDescription>Суммарное время ожидания и удержания по часам (минуты)</CardDescription>
          </CardHeader>
          <CardContent>
            <SingleBarChart
              data={waitByHourChartData}
              axisFormatter={minutesAxisFormatter}
              tooltipFormatter={minutesTooltipFormatter}
              labelFormatter={minutesAxisFormatter}
              seriesLabel="Минуты ожидания"
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-left">
            <CardTitle>{texts.funnel?.title ?? 'Метрики допродаж по сотрудникам'}</CardTitle>
            <CardDescription>
              {texts.funnel?.description ?? 'Заказы, попытки и успешные допродажи по операторам'}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-base">
              <span>Команда</span>
              <span className="text-sm text-muted-foreground">
                Успешность: {formatPercent(100 * teamUpsellMetrics.successes / teamUpsellMetrics.orders)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <UpsellMetricRow label="Заказы" value={teamUpsellMetrics.orders} />
            <UpsellMetricRow label="Попытки допродажи" value={teamUpsellMetrics.attempts} />
            <UpsellMetricRow label="Успешные допродажи" value={teamUpsellMetrics.successes} />
            <UpsellMetricRow label="Доля попыток допродаж" value={teamUpsellMetrics.attemptRate} isPercent />
            <UpsellMetricRow label="Доля успешных допродаж" value={teamUpsellMetrics.successRate} isPercent />
          </CardContent>
        </Card>

        {filteredUpsellItems.map((item, index) => (
          <Card key={item.employeeId ?? item.employeeName ?? index}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 text-base">
                <span>{item.employeeName ?? 'Неизвестно'}</span>
                <span className="text-sm text-muted-foreground">
                  Успешность: {formatPercent(100 * item.upsellSuccesses / item.totalOrders)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <UpsellMetricRow label="Заказы" value={item.totalOrders} />
              <UpsellMetricRow label="Попытки допродажи" value={item.upsellAttempts} />
              <UpsellMetricRow label="Успешные допродажи" value={item.upsellSuccesses} />
              <UpsellMetricRow label="Доля попыток допродаж" value={item.attemptRate} isPercent />
              <UpsellMetricRow label="Доля успешных допродаж" value={item.successRate} isPercent />
            </CardContent>
          </Card>
        ))}
      </div>

    </div>
  );
};

const KpiCard = ({ title, description, value, icon: Icon }) => (
  <Card>
    <CardHeader className="flex-row items-start justify-between space-y-0 p-4 pb-2">
      <div>
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
    </CardContent>
  </Card>
);

const formatMetric = (metric) => {
  if (!metric) return '—';
  if (metric.formatted) return metric.formatted;
  if (typeof metric.current === 'number') return metric.current.toFixed(1);
  return metric.current ?? '—';
};

const PhoneIcon = (props) => <Activity {...props} />;

const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return '—';
  return `${numeric.toFixed(1)}%`;
};

const UpsellMetricRow = ({ label, value, isPercent = false }) => {
  const parsedValue = Number(value) || 0;
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="font-medium">{label}</span>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {isPercent ? `${parsedValue.toFixed(1)}%` : parsedValue.toLocaleString('ru-RU')}
        </span>
      </div>
    </div>
  );
};

export default EmployeeAnalytics;
